import { EVENT_SOCKET_OPEN, EVENT_SOCKET_CLOSE, PING_CODE, PONG_CODE, EVENT_SOCKET_HEART_CHECK } from '@/utils/constant'
import { getNetworkInfo } from './navigator'
import { Singleflight } from '@voghion/fe-util'

class Socket {
  /**
   * @description: 初始化实例属性，保存参数
   */
  constructor(options) {
    // 要连接的后端地址
    this.url = options.url
    // 连接open执行方法
    this.openCallback = options.openCallback
    // 接收到信息后执行的方法
    this.callback = options.receivedCallback
    // 成功关闭执行方法
    this.closeCallback = options.closeCallback
    // 报错后执行方法
    this.errorCallback = options.errorCallback
    // 日志回调
    this.logCallback = options.logCallback
    // 状态切换回调
    this.readyStateChangeCallback = options.readyStateChangeCallback
    // connect 前置操作，支持 promise
    this.beforeConnect = options.beforeConnect
    this.name = options.name || 'default'
    this.ws = null
    // 当前状态
    this._readyState = 3
    // 外部控制是否开启，如果是自动断开，不再自动重连
    this.enable = true
    this.pingInterval = null
    // 心跳检测频率
    this.timeout = options.timeout || 20000
    this.isHeart = options.isHeart
    this.loginMsg = options.loginMsg
    this.isReconnection = options.isReconnection
    this.errReconnectTime = options.errReconnectTime || 5
    this.reconnectionCount = 1
    this.delay = options.delay || 5000
    this.reconnectTimer = 0
    /**
     * 是否有错误
     */
    this.isError = false
    this.errorMsg = ''
    this.connectTimer = 0
    this.connectTimeout = options.connectTimeout || 30000 // 连接超时时间
    /**
     * 心跳数据
     */
    this.heartCheckState = {
      pingCount: 0,
      pongCount: 0,
      pingTime: 0,
      pongTime: 0
    }
    /**
     * 自动重连计时器
     */
    this.autoReconnectTimer = 0
  }

  /**
   * 状态，open | close
   */
  get status() {
    return this._readyState === 1 ? 'open' : 'close'
  }

  /**
   * socket 状态
   * @link https://developer.mozilla.org/zh-CN/docs/Web/API/WebSocket/readyState
   */
  get readyState() {
    return this._readyState
  }

  set readyState(val) {
    const oldVal = this._readyState
    const isChange = val !== oldVal
    this._readyState = val
    if (isChange && typeof this.readyStateChangeCallback === 'function') {
      this.readyStateChangeCallback(val, oldVal)
    }
  }

  /**
   * 建立连接
   * @param {*} data
   * @returns
   */
  connect(data = null) {
    if (!data) {
      data = this.loginMsg
    } else {
      this.loginMsg = data
    }
    const singleflight = Singleflight.getInstance()
    return singleflight.do(`socket_connect_${JSON.stringify(data || {})}`, () => {
      return new Promise((resolve) => {
        this.enable = true
        // 正在连接和已连接直接返回
        if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) {
          return resolve(true)
        }
        this.readyState = 0
        let beforeConnectRes = true
        if (typeof this.beforeConnect === 'function') {
          beforeConnectRes = this.beforeConnect()
        }
        if (typeof beforeConnectRes === 'object' && beforeConnectRes.then) {
          beforeConnectRes.then((res) => {
            if (res) {
              this._innerConnect(data)
              resolve(true)
            } else {
              this.readyState = 3
              resolve(false)
            }
          })
        } else {
          if (beforeConnectRes) {
            this._innerConnect(data)
            resolve(true)
          } else {
            this.readyState = 3
            resolve(false)
          }
        }
      })
    })
  }

  _innerConnect(data) {
    const startTime = Date.now()
    try {
      if (this.ws) {
        // 解绑相关事件，避免旧的 socket 回调晚于新的 socket 导致回调错乱
        this.ws.onopen = undefined
        this.ws.onclose = undefined
        this.ws.onmessage = undefined
        this.ws.onerror = undefined
        if (this.ws.readyState === 2) {
          // 如果连接正在关闭中，主动触发 close 回调，因为旧的 socket onclose 回调已解绑
          this.onClose(
            new CloseEvent('close', {
              code: 1006,
              reason: this.errorMsg
            })
          )
        }
      }
      this.ws = new WebSocket(this.url)
      // 建立连接
      this.ws.onopen = (e) => {
        if (this.connectTimer) {
          clearTimeout(this.connectTimer)
          this.connectTimer = 0
        }
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer)
          this.reconnectTimer = 0
        }
        this.errorMsg = ''
        this.heartCheckState = {
          pingCount: 0,
          pongCount: 0,
          pingTime: 0,
          pongTime: 0
        }
        if (this.isHeart) {
          // 心跳
          this.heartCheck()
        }
        if (typeof this.openCallback === 'function') {
          // 如果是重启，已重启的次数恢复为1
          this.reconnectionCount = 1
          this.openCallback(e)
        }
        const endTime = Date.now()
        this.logInfo(EVENT_SOCKET_OPEN, {
          elapsed_time: endTime - startTime
        })
        this.readyState = this.ws.readyState
        // 给后台发送数据
        if (data !== undefined) {
          /* ****************************************这里设置传输内容***************************************************************** */
          return this.ws.send(JSON.stringify(data))
        }
      }
      // 接受服务器返回的信息
      this.ws.onmessage = (e) => {
        if (typeof this.callback === 'function') {
          if (typeof e.data === 'string' && e.data.startsWith(PONG_CODE)) {
            const data = JSON.parse(e.data.replace(`${PONG_CODE}:`, '').trim())
            this.receivePong(data)
          }
          return this.callback(e.data)
        } else {
          console.log('参数的类型必须为函数')
        }
      }
      // 关闭连接
      this.ws.onclose = (e) => {
        this.onClose(e)
      }
      // 报错，按照规范，error 后一定会触发 close
      this.ws.onerror = (e) => {
        this.isError = true
        if (this.connectTimer) {
          this.errorMsg = 'connect error'
        }
        if (typeof this.errorCallback === 'function') {
          this.errorCallback(e)
        }
      }
      this.connectTimer = setTimeout(() => {
        this.closeAndReconnect('connect timeout')
      }, this.connectTimeout)
    } catch (e) {
      this.errorCallback(e)
      console.log('长连接前端失败', e)
    }
  }

  onClose(e) {
    this.resetHeart()
    if (this.connectTimer) {
      clearTimeout(this.connectTimer)
      this.connectTimer = 0
    }
    if (this.autoReconnectTimer) {
      clearTimeout(this.autoReconnectTimer)
      this.autoReconnectTimer = 0
    }
    this.readyState = 3
    if (this.enable && !this.isError) {
      this.isError = true
    }
    this.logInfo(EVENT_SOCKET_CLOSE, {
      code: e.code || 1000,
      reason: this.errorMsg || e.reason || '',
      is_error: this.isError,
      network_info: getNetworkInfo()
    })
    this.errorMsg = ''
    let willReconnect = false
    if (this.isError) {
      // 只要不是主动断开，都尝试自动重连
      willReconnect = this.autoReconnect()
    }
    e.isError = this.isError
    e.willReconnect = willReconnect
    e.reconnectionCount = this.reconnectionCount
    this.isError = false
    if (typeof this.closeCallback === 'function') {
      this.closeCallback(e)
    }
  }

  /**
   * 记录日志
   */
  logInfo(event, params = {}) {
    if (this.logCallback) {
      this.logCallback({
        type: 'info',
        event,
        params
      })
    }
  }

  /**
   * 记录错误
   * @param {*} description
   * @param {*} params
   */
  logError(description, params = {}) {
    if (this.logCallback) {
      this.logCallback({
        type: 'error',
        description,
        params
      })
    }
  }

  /**
   * 发送消息
   * @param {*} msgStr
   * @returns
   */
  sendMsg(msgStr) {
    return this.ws && this.ws.send(msgStr)
  }

  /**
   * 重置心跳检测
   * @returns
   */
  resetHeart() {
    clearInterval(this.pingInterval)
    return this
  }

  /**
   * 开始心跳检测
   */
  heartCheck() {
    this.pingInterval = setInterval(() => {
      if (this.ws.readyState === 1) {
        /* ************************************************这里设置检测传输的内容**************************************** */
        this.heartCheckState.pingCount++
        this.heartCheckState.pingTime = Date.now()
        if (!this.heartCheckState.pongTime) {
          this.heartCheckState.pongTime = this.heartCheckState.pingTime
        }
        console.log(
          `发送${PING_CODE}`,
          `${new Date().getHours()}:${new Date().getMinutes()}:${new Date().getSeconds()}`,
          `记数：${this.heartCheckState.pingCount}`
        )
        this.ws.send(
          `${PING_CODE}:${JSON.stringify({
            count: this.heartCheckState.pingCount,
            timestamp: this.heartCheckState.pingTime
          })}`
        )
        /* **************************************************************************************************************** */
        if (this.heartCheckState.pongTime < this.heartCheckState.pingTime - this.timeout * 3) {
          // 如果多次检测都没收到响应，主动断开连接后再重连
          this.closeAndReconnect('ping pong timeout')
        }
      }
    }, this.timeout)
  }

  /**
   * 重连
   */
  reconnect(delay = 0) {
    if (delay <= 0) {
      this.connect()
    } else {
      this.reconnectTimer = setTimeout(() => {
        this.connect()
      }, delay)
    }
  }

  /**
   * 自动重连
   */
  autoReconnect() {
    if (
      this.enable &&
      this.isReconnection &&
      (this.reconnectionCount <= this.errReconnectTime || this.errReconnectTime <= 0)
    ) {
      if (this.reconnectionCount <= 1 && navigator.onLine) {
        /**
         * 首次断开快速重连
         */
        console.log(`500 ms 后重新连接~`)
        this.reconnect(500)
      } else {
        /**
         * 再次断开
         */
        console.log(`${this.delay} ms 后重新连接~`)
        this.reconnect(this.delay)
      }
      this.reconnectionCount++
      return true
    }
    return false
  }

  /**
   * 收到 pong
   */
  receivePong(data) {
    this.heartCheckState.pongCount++
    this.heartCheckState.pongTime = Date.now()
    if (data.timestamp) {
      this.logInfo(EVENT_SOCKET_HEART_CHECK, {
        count: data.count,
        rtt: this.heartCheckState.pongTime - data.timestamp,
        network_info: getNetworkInfo()
      })
    }
    console.log(
      `收到${PONG_CODE}`,
      `${new Date().getHours()}:${new Date().getMinutes()}:${new Date().getSeconds()}`,
      `记数：${this.heartCheckState.pongCount}`
    )
  }

  /**
   * 主动断开
   * @param {*} code
   * @param {*} reason
   * @returns
   */
  close(code = 1000, reason = 'close by user') {
    this.enable = false
    this._innerClose(code, reason)
  }

  _innerClose(code, reason = '') {
    this.resetHeart()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = 0
    }
    if (this.connectTimer) {
      clearTimeout(this.connectTimer)
      this.connectTimer = 0
    }
    this.ws.close(code, reason)
    this.readyState = this.ws.readyState
  }

  /**
   * 断开并重连
   * @param {*} code
   * @param {*} reason
   */
  closeAndReconnect(errorMsg = '') {
    this.isError = true
    this.errorMsg = errorMsg
    this._innerClose(1000, errorMsg)
    this.autoReconnectTimer = setTimeout(() => {
      this.autoReconnect()
    }, 1000)
  }
}

export default Socket
